import React from 'react';
import { FormattedMessage } from 'react-intl';

import { MAX_SALARY, MIN_SALARY, RADIUS_OPTIONS } from '@/domain/search/constants';
import { SetQueryParamsInput } from '@/domain/search/contexts';
import {
  JobSearchFilterCollectionSplitBenefitsFragment,
  JobSearchQueryInput,
  JobSearchQueryLocation,
  JobSearchQueryLocationInput,
  Maybe,
} from '@/graphql/generated';

import { EntityIdJobSearchFilter } from '../filters/types';

const filterTypes = {
  JobFilterBenefitEmployeePerk: 'benefit',
  JobFilterBenefitWorkingCulture: 'benefit',
  JobFilterCareerLevel: 'careerLevel',
  JobFilterCity: 'city',
  JobFilterCompany: 'company',
  JobFilterCountry: 'country',
  JobFilterDiscipline: 'discipline',
  JobFilterEmploymentType: 'employmentType',
  JobFilterIndustry: 'industry',
  JobFilterSalary: 'salary',
  JobFilterRemoteOption: 'remoteOption',
} as const;

export const typeToFilter = Object.entries(filterTypes).reduce(
  (result, [filter, type]) => ({ ...result, [type]: filter }),
  {}
);

type MaybeSalary = number | null | undefined;

export const isDefined = (value: unknown): boolean => value !== undefined && value !== null;

export const hasSalary = (min: MaybeSalary, max: MaybeSalary): boolean =>
  (isDefined(min) && min !== MIN_SALARY) || (isDefined(max) && max !== MAX_SALARY);

export const parseFilterCollectionToFilterQuery = (
  filterCollection: Array<JobSearchFilterCollectionSplitBenefitsFragment | null>
): JobSearchQueryInput['filter'] => {
  const initial = {
    benefit: { id: [] },
    careerLevel: { id: [] },
    city: { id: [] },
    country: { id: [] },
    company: { id: [] },
    discipline: { id: [] },
    employmentType: { id: [] },
    industry: { id: [] },
    salary: undefined,
    projob: undefined,
    remoteOption: { id: [] },
  };

  return filterCollection.reduce((result, filter) => {
    if (!filter || !filter.__typename) {
      return result;
    }

    const indexKey = filterTypes[filter.__typename];

    if (!indexKey) {
      return result;
    }

    // Special cases
    if (filter.__typename === 'JobFilterSalary') {
      return {
        ...result,
        salary: hasSalary(filter.min, filter.max) ? { min: filter.min, max: filter.max } : undefined,
      };
    }

    return { ...result, [indexKey]: { id: [...result[indexKey].id, (filter as EntityIdJobSearchFilter).entityId] } };
  }, initial);
};

export const parseFiltersToQuery = (
  filters: Maybe<JobSearchFilterCollectionSplitBenefitsFragment>[]
): Partial<SetQueryParamsInput> =>
  filters.reduce(
    (result, filter) => {
      if (!filter || !filter.__typename) {
        return result;
      }

      const typename = filter.__typename;
      if (!typename) {
        return result;
      }

      const filterTypeName = filterTypes[typename];
      switch (filter.__typename) {
        case 'JobFilterSalary': {
          return {
            ...result,
            [filterTypeName]: hasSalary(filter.min, filter.max) ? [filter.min, filter.max] : undefined,
          };
        }
        default: {
          if (!('entityId' in filter)) {
            return result;
          }

          return {
            ...result,
            [filterTypeName]: result[filterTypeName] ? [...result[filterTypeName], filter.entityId] : [filter.entityId],
          };
        }
      }
    },
    {
      salary: undefined,
      company: undefined,
      employmentType: undefined,
      careerLevel: undefined,
      discipline: undefined,
      industry: undefined,
      benefit: undefined,
      country: undefined,
      city: undefined,
      remoteOption: undefined,
    } as Partial<SetQueryParamsInput>
  );

export const parserLocationQueryToLocationInput = (
  location?: Maybe<JobSearchQueryLocation>
): Maybe<JobSearchQueryLocationInput> => {
  if (!location) {
    return null;
  }

  const { cityId, city, text, radius } = location;
  return { cityId: city?.id ?? cityId, text: city?.name ?? text, radius };
};

export const getRadiusOptions = (cityName: string): Record<string, JSX.Element> => {
  const options = {
    0: <FormattedMessage id="JOB_SEARCH_RADIUS_FILTER_DESCRIPTION_DEFAULT" values={{ city: cityName }} />,
  };

  RADIUS_OPTIONS.forEach((radius) => {
    if (radius === 0) return;

    options[radius] = (
      <FormattedMessage id="JOB_SEARCH_RADIUS_FILTER_DESCRIPTION_VALUE" values={{ city: cityName, value: radius }} />
    );
  });

  return options;
};
